import * as yup from 'yup'
import React from 'react'
import { Divider } from '../../../../components/divider'
import { suffixes, sex } from '../../../../choices'
import { v4 as uuidv4 } from 'uuid'

import {
  addressLabel,
  address2Label,
  dobLabel,
  firstNameLabel,
  lastNameLabel,
  middleNameLabel,
  optionalLabel,
  sexLabel,
  suffixLabel
} from '../../../../strings/labels'
import {
  addressLengthMessage,
  victimAgeMessage,
  cityLengthMessage,
  firstNameMessage,
  firstNameLengthMessage,
  lastNameMessage,
  lastNameLengthMessage,
  maxAgeMessage,
  middleNameMessage,
  middleNameLengthMessage,
  zipMessage,
  zipLengthMessage
} from '../../../../strings/messages'
import { alphaRegex, zipRegex } from '../../../../strings/regex'
import { dateYupOptional } from '../../../../validators/dateYup'
import { dateToString } from '../../../../utilities'

const NameHeading = () => <h2 className="ds-u-margin-bottom--0">Name</h2>

export const ihsVictimPage = propOverrides => {
  const props = {
    index: 0,
    maxIndex: 9,
    pageId: 'ihs_victim_page',
    title: 'Victim information',
    description:
      'Victim information helps us conduct our investigations. Please provide as much detail as you can. You will be able to enter multiple victims.',
    pageAfterVictim: 'know_parent_guardian',
    ...propOverrides
  }

  const ContentDivider = () => <Divider />

  const HomeAddressHeading = () => (
    <h2 className="ds-u-margin-bottom--0">Home Address</h2>
  )

  // Create unique field names based on the field name and the index value.
  const pageId = `${props.pageId}_${props.index}`
  const victim_uuid = `victim_uuid_${props.index}`
  const victim_first_name = `victim_first_name_${props.index}`
  const victim_middle_name = `victim_middle_name_${props.index}`
  const victim_last_name = `victim_last_name_${props.index}`
  const victim_suffix = `victim_suffix_${props.index}`
  const victim_dob = `victim_dob_${props.index}`
  const victim_age = `victim_age_${props.index}`
  const victim_sex = `victim_sex_${props.index}`
  const victim_address = `victim_address_${props.index}`

  let page = {
    id: pageId,
    title: props.title,
    description: props.description,
    validation: yup.object().shape({
      [victim_first_name]: yup
        .string()
        .matches(alphaRegex, {
          message: firstNameMessage,
          excludeEmptyString: false
        })
        .max(30, firstNameLengthMessage)
        .required(firstNameMessage),
      [victim_middle_name]: yup
        .string()
        .matches(alphaRegex, {
          message: middleNameMessage,
          excludeEmptyString: false
        })
        .max(30, middleNameLengthMessage),
      [victim_last_name]: yup
        .string()
        .matches(alphaRegex, {
          message: lastNameMessage,
          excludeEmptyString: false
        })
        .max(30, lastNameLengthMessage)
        .required(lastNameMessage),
      [victim_dob]: dateYupOptional(),
      [victim_age]: yup.number().when([victim_dob], {
        is: val => dateToString(val) != null,
        then: yup
          .number()
          .typeError(victimAgeMessage)
          .positive(maxAgeMessage)
          .integer(maxAgeMessage)
          .max(120, maxAgeMessage),
        otherwise: yup
          .number()
          .typeError(victimAgeMessage)
          .positive(maxAgeMessage)
          .integer(maxAgeMessage)
          .max(120, maxAgeMessage)
          .required(victimAgeMessage)
      }),
      [victim_address]: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      })
    }),
    fields: [
      {
        type: 'hidden',
        className: 'ds-u-display--none ds-u-visibility--hidden',
        value: uuidv4(),
        name: victim_uuid,
        label: 'Victim Unique ID',
        disabled: true
      },
      {
        type: 'content',
        content: NameHeading,
        name: 'name_information'
      },
      {
        type: 'text',
        label: firstNameLabel,
        name: victim_first_name
      },
      {
        type: 'text',
        label: middleNameLabel,
        name: victim_middle_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: lastNameLabel,
        name: victim_last_name
      },
      {
        type: 'select',
        label: suffixLabel,
        name: victim_suffix,
        choices: [
          {
            value: '',
            label: ''
          },
          ...suffixes()
        ],
        size: 'small',
        requirementLabel: optionalLabel
      },
      {
        type: 'date',
        label: dobLabel,
        name: victim_dob,
        requirementLabel: optionalLabel,
        overrideDefaultMinMax: true
      },
      {
        type: 'number',
        name: victim_age,
        label: 'Age',
        hint: 'Please estimate the age if date of birth is not known.',
        max: 120,
        size: 'small'
      },
      {
        type: 'select',
        label: sexLabel,
        name: victim_sex,
        choices: [
          {
            value: '',
            label: ''
          },
          ...sex()
        ],
        size: 'small',
        requirementLabel: optionalLabel
      },
      {
        type: 'content',
        content: ContentDivider
      },
      {
        type: 'content',
        content: HomeAddressHeading,
        name: 'home_address_information'
      },
      {
        type: 'address',
        name: victim_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      }
    ]
  }

  // When not on the last page, give option to add another victim
  if (props.index < props.maxIndex) {
    const add_victim = `add_victim_${props.index}`
    const nextVictimPage = `${props.pageId}_${props.index + 1}`
    page.fields.push({
      type: 'checkbox',
      label: '',
      name: add_victim,
      choices: [
        {
          label: `Add another victim (up to ${props.maxIndex + 1} victims)`,
          value: 'True'
        }
      ],
      requirementLabel: optionalLabel
    })
    page.action = {
      type: 'goto',
      pageId: values => {
        const add = values[add_victim]
        return add.length > 0 ? nextVictimPage : props.pageAfterVictim
      }
    }
  }

  return page
}
